import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, adminAuth, authQuery } from "@/lib/api";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, ClipboardCheck, Printer, ArrowRight, ArrowLeft, Sparkles, Send, Download, CalendarCheck, Microscope, Video } from "lucide-react";
import { toast } from "sonner";

const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || "";
const MICROSCOPIC_URL = process.env.REACT_APP_MICROSCOPIC_URL || "";
const VIRTUAL_URL = process.env.REACT_APP_VIRTUAL_CONSULT_URL || "";

function FollowupCTAs() {
  const cards = [
    { icon: CalendarCheck, title: "Book consultation", body: "In-clinic or virtual session with your KCIS practitioner.", url: BOOKING_URL, testId: "report-cta-booking" },
    { icon: Microscope, title: "Schedule microscopic analysis", body: "High-resolution scalp imaging to track progress closely.", url: MICROSCOPIC_URL, testId: "report-cta-microscopic" },
    { icon: Video, title: "Virtual follow-up", body: "A focused 1:1 video check-in to refine your protocol.", url: VIRTUAL_URL, testId: "report-cta-virtual" },
  ].filter(c => c.url);
  if (cards.length === 0) return null;
  return (
    <div className="mt-12" data-testid="report-followup-ctas">
      <div className="kcis-overline">Continue your wellness journey</div>
      <h3 className="font-display text-xl tracking-tight text-slate-900 mt-1">Suggested next steps</h3>
      <div className="grid md:grid-cols-3 gap-3 mt-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <a key={c.testId} href={c.url} target="_blank" rel="noopener noreferrer"
              className="kcis-card p-5 hover:border-purple-300 transition group" data-testid={c.testId}>
              <div className="h-9 w-9 rounded-xl bg-purple-50 text-purple-900 flex items-center justify-center">
                <Icon className="h-5 w-5" />
              </div>
              <div className="font-display text-base text-slate-900 mt-3 group-hover:text-purple-900">{c.title}</div>
              <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{c.body}</p>
            </a>
          );
        })}
      </div>
    </div>
  );
}

const sectionIcons = {
  "Client Summary": "01",
  "Key Wellness Patterns": "02",
  "Hair & Scalp Considerations": "03",
  "Functional Wellness Considerations": "04",
  "Recommended Service Path": "05",
  "Recommended Tea Support": "06",
  "12-Week Protocol Draft": "07",
  "Disclaimer": "08",
};

export default function Report() {
  const { clientId: assessmentId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const isAdmin = Boolean(adminAuth.getToken());

  const load = async () => {
    setLoading(true);
    try {
      const { data: a } = await api.get(`/assessments/${assessmentId}`);
      setAssessment(a);
      try {
        const { data: r } = await api.get(`/assessments/${assessmentId}/report`);
        setReport(r);
      } catch {
        // not yet generated
      }
    } catch (e) {
      toast.error("Could not load report");
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: r } = await api.post(`/assessments/${assessmentId}/report`);
      setReport(r);
      toast.success("Protocol generated and emailed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/assessments/${assessmentId}/send-report`);
      if (data.status === "sent") {
        toast.success(`Report sent to ${assessment.email}`);
      } else if (data.status === "skipped") {
        toast.info("Email delivery not configured. PDF saved — use Download PDF.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/assessments/${assessmentId}/report.pdf?download=1&${authQuery()}`;
    window.open(url, "_blank", "noopener");
    toast.success("PDF opened — your browser will save or display it");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assessmentId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav
        right={
          <Button
            variant="outline"
            className="rounded-full border-slate-300"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/assessments/${assessmentId}/report.pdf?${authQuery()}`;
              window.open(url, "_blank", "noopener");
            }}
            data-testid="report-print-btn"
          >
            <Printer className="h-4 w-4 mr-2" /> Print / PDF
          </Button>
        }
      />

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        {isAdmin && (
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-purple-900 transition-colors mb-6"
            data-testid="report-back-to-admin"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Admin Dashboard
          </Link>
        )}
        <div className="kcis-card p-8 sm:p-12 print:shadow-none print:border-0">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="kcis-overline text-purple-900">KCIS Protocol Report</div>
              <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900 mt-2">
                Wellness summary for {assessment?.name}
              </h1>
              <div className="mt-3 flex flex-wrap gap-2 items-center text-sm text-slate-500">
                <span>{assessment?.email}</span>
                <span>·</span>
                <span>Age {assessment?.age}</span>
                <span>·</span>
                <Badge className="bg-purple-50 text-purple-900 hover:bg-purple-50 border border-purple-100">
                  {report ? "Generated" : "Pending"}
                </Badge>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!report && (
                <Button onClick={generate} disabled={generating} className="rounded-full bg-purple-900 hover:bg-purple-800 text-white" data-testid="report-generate-btn">
                  {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Generating</>) : (<><Sparkles className="h-4 w-4 mr-2"/>Generate</>)}
                </Button>
              )}
              {isAdmin && report && (
                <>
                  <Button
                    onClick={sendReport}
                    disabled={sending}
                    className="rounded-full bg-purple-900 hover:bg-purple-800 text-white"
                    data-testid="report-send-btn"
                  >
                    {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Send Report to Client
                  </Button>
                  <Button
                    onClick={downloadPdf}
                    variant="outline"
                    className="rounded-full border-slate-300"
                    data-testid="report-download-pdf-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </>
              )}
              <Link to={`/client/followup/${assessmentId}`}>
                <Button variant="outline" className="rounded-full border-slate-300" data-testid="report-followup-btn">
                  <ClipboardCheck className="h-4 w-4 mr-2" /> 7-day Follow-up
                </Button>
              </Link>
            </div>
          </div>

          {!report && (
            <div className="mt-12 rounded-xl border border-dashed border-slate-300 p-10 text-center">
              <FileText className="h-8 w-8 mx-auto text-slate-400" />
              <p className="mt-3 text-slate-600">Your protocol report has not been generated yet.</p>
              <Button onClick={generate} disabled={generating} className="mt-5 rounded-full bg-purple-900 hover:bg-purple-800 text-white">
                {generating ? "Generating..." : "Generate now"}
              </Button>
            </div>
          )}

          {report && (
            <div className="mt-12 space-y-12" data-testid="report-content">
              {report.sections.map((s, i) => (
                <div key={i} className="kcis-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="flex items-baseline gap-4 border-b border-slate-200 pb-3">
                    <span className="font-display text-2xl text-purple-900/60">{sectionIcons[s.title] || `0${i + 1}`}</span>
                    <h2 className="font-display text-xl sm:text-2xl tracking-tight text-slate-900">{s.title}</h2>
                  </div>
                  <div className="mt-5 text-slate-700 leading-relaxed whitespace-pre-line text-[15px]">
                    {s.content}
                  </div>
                </div>
              ))}

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-6 text-xs text-slate-500 mt-12">
                Generated {new Date(report.generated_at).toLocaleString()} · KCIS™ ·
                Educational wellness support, not medical advice.
              </div>

              <FollowupCTAs />
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-center">
          <Link to="/">
            <Button variant="ghost" className="text-slate-600 hover:text-purple-900">
              Return home <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
